import AdminPermission from "components/admin/AdminPermission";
import useAdminAccess from "hooks/useAdminAccess";
import {
  ActionCategory,
  GamificationAction,
  gamificationApi,
} from "api/admin/gamification";
import { FormEvent, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import {
  Button,
  Drawer,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  SectionCard,
  errorMessage,
  fieldClass,
  formatLabel,
} from "./components";

const categoryTone: Record<ActionCategory, string> = {
  revenue_generating: "bg-emerald-50 text-emerald-700",
  revenue_critical: "bg-orange-50 text-orange-700",
  trust_building: "bg-blue-50 text-blue-700",
  stickiness: "bg-violet-50 text-violet-700",
  one_time_milestone: "bg-amber-50 text-amber-700",
};

export default function EloActions() {
  const { can } = useAdminAccess();
  const canReadOptions = can("gamification.overview.view") || can("gamification.actions.view");
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<GamificationAction>();
  const [category, setCategory] =
    useState<ActionCategory>("revenue_generating");
  const [points, setPoints] = useState("");
  const [reason, setReason] = useState("");
  const actions = useQuery("gamification-actions", gamificationApi.listActions, { enabled: can("gamification.actions.view"),
    retry: false,
  });
  const options = useQuery(
    "gamification-configuration-options",
    gamificationApi.getConfigurationOptions,
    { enabled: canReadOptions, retry: false },
  );
  const save = useMutation(
    ({ actionId }: { actionId: number }) =>
      gamificationApi.updateAction(actionId, {
        category,
        eloPoints: Number(points),
        reason: reason || undefined,
      }),
    {
      onSuccess: () => {
        toast.success("Action updated");
        queryClient.invalidateQueries("gamification-actions");
        queryClient.invalidateQueries("gamification-actions-overview");
        setSelected(undefined);
      },
      onError: (error) => {
        toast.error(errorMessage(error));
      },
    },
  );

  const open = (action: GamificationAction) => {
    setSelected(action);
    const currentCategory = options.data?.actionCategories.find(
      (option) => option.code === action.eloCategory,
    );
    setCategory(currentCategory?.code ?? "revenue_generating");
    setPoints(String(action.eloMapping?.eloPoints ?? ""));
    setReason("");
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (selected) save.mutate({ actionId: selected.id });
  };

  return (
    <>
      <SectionCard
        title="Actions"
        description="These are system-owned product actions. Configure their points and category here; you cannot create ad-hoc actions."
      >
        {actions.isLoading || options.isLoading ? (
          <LoadingState rows={5} />
        ) : actions.error || options.error ? (
          <ErrorState
            error={actions.error || options.error}
            onRetry={() => {
              if (can("gamification.actions.view")) actions.refetch();
              if (canReadOptions) options.refetch();
            }}
          />
        ) : !actions.data?.actions.length ? (
          <EmptyState
            title="No actions available"
            body="System-owned product actions will appear here when they are available."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 text-left">
              <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-5 py-3">Action</th>
                  <th className="px-5 py-3">Category</th>
                  <th className="px-5 py-3">Base ELO points</th>
                  <th className="px-5 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {actions.data.actions.map((action) => {
                  const actionCategory = options.data?.actionCategories.find(
                    (option) => option.code === action.eloCategory,
                  );
                  return (
                    <tr key={action.id} className="hover:bg-gray-50/70">
                      <td className="px-5 py-4">
                        <p className="font-semibold text-gray-950">{action.name}</p>
                        <p className="mt-1 text-xs text-gray-400">{action.eventKey ?? action.code}</p>
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                            actionCategory
                              ? categoryTone[actionCategory.code]
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {actionCategory?.name ?? formatLabel(action.eloCategory)}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-sm font-semibold text-gray-900">
                        {action.eloMapping?.eloPoints?.toLocaleString() ?? "Not set"}
                      </td>
                      <td className="px-5 py-4 text-right">
                        <AdminPermission permission={"gamification.actions.edit"}>
                          <Button variant="quiet" onClick={() => open(action)}>
                            Edit
                          </Button>
                        </AdminPermission>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <AdminPermission permission={"gamification.actions.edit"}>
        <Drawer
          open={Boolean(selected)}
          title="Edit action"
          description={selected?.name}
          onClose={() => setSelected(undefined)}
        >
          <form className="space-y-5" onSubmit={submit}>
            <Field
              label="Category"
              required
              hint={
                options.data?.actionCategories.find((option) => option.code === category)
                  ?.description
              }
            >
              <select
                className={fieldClass}
                value={category}
                onChange={(event) => setCategory(event.target.value as ActionCategory)}
              >
                {options.data?.actionCategories.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.name}
                    {option.qualifiesForStreak ? " · qualifies for streak" : ""}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Base ELO points"
              hint="Active streaks can apply the member's tier multiplier to this base award."
              required
            >
              <input
                required
                min={1}
                max={1_000_000}
                type="number"
                className={fieldClass}
                value={points}
                onChange={(event) => setPoints(event.target.value)}
              />
            </Field>
            <Field label="Reason for change">
              <textarea
                rows={3}
                className={fieldClass}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </Field>
            <div className="flex justify-end gap-3 border-t border-gray-100 pt-5">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setSelected(undefined)}
              >
                Cancel
              </Button>
              <Button disabled={save.isLoading} type="submit">
                {save.isLoading ? "Saving…" : "Save action"}
              </Button>
            </div>
          </form>
        </Drawer>
      </AdminPermission>
    </>
  );
}
